import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { MoreVert, RemoveCircle } from '@mui/icons-material'
import {
  Box,
  Button,
  Container,
  IconButton,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  DialogActions,
  DialogTitle,
  DialogContent,
  Divider,
} from '@mui/material'
import {
  Mutation,
  Query,
  RegistrationKey,
  RetailerAccount,
  User,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import { VALIDATE_TOKEN } from '@src/components/auth/login/LoginForm'
import {
  JOIN_RETAILER,
  CREATE_RETAILER,
  ADD_RETAILER_TO_RETAILER_ACCOUNT,
} from '@src/components/enrollment/BusinessSelectDialog'
import BusinessSelector from '@src/components/shared/BusinessSelector'
import SmartImage from '@src/components/shared/SmartImage'
import BottomAppBarSpacer from '@src/lib/BottomAppBarSpacer'
import ChildSpacer from '@src/lib/ChildSpacer'
import cloudinary from '@src/lib/Cloudinary'
import ConfirmDialog from '@src/lib/ConfirmDialog'
import LinkChild from '@src/lib/LinkChild'
import useModalState from '@src/lib/useModalState'
import { actions } from '@src/stores'

import { GET_JWT } from '../auth/saneLogin'
import MobileFullScreenDialog from '../shared/MobileFullScreenDialog'

import manageBrandsPaths from './manageBrandsPaths'
import useManageBrandDialog from './useManageBrandDialog'

const GET_PENDING_INVITES = gql`
  query getPendingInvitesPage($userId: ID!) {
    getUser(
      id: $userId
      fields: ["registration_keys", "registration_keys.brand"]
    ) {
      id
      registration_keys {
        id
        link_id
        brand_id
        retailer_id
        brand {
          id
          name
          logo_url
        }
      }
    }
  }
`

const DISABLE_REGISTRATION_KEY = gql`
  mutation disableRegistrationKeyPendingInvitesPage(
    $brand_id: Int!
    $user_id: Int!
  ) {
    createUserBrandRejection(
      user_brand_rejection: { brand_id: $brand_id, user_id: $user_id }
    ) {
      id
    }
  }
`

const GET_BRAND_SOCIAL_SETTING = gql`
  query getBrandSocialSettings($brandId: ID!, $params: BrandParams) {
    getBrand(id: $brandId, params: $params) {
      id
      settings {
        social_account_synchronization_on_creation
      }
    }
  }
`

const useStyles = makeStyles()((theme) => ({
  image: {
    width: 150,
    margin: theme.spacing(2),
    objectFit: 'contain',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}))

const PendingInvites: React.FC<React.PropsWithChildren> = (props) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const currentUser = useSelector(
    (state: { auth: { user: User } }) => state.auth.user,
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [acceptedRetailerId, setAcceptedRetailerId] = useState<string | null>(
    null,
  )
  const [acceptedRetailerAccountId, setAcceptedRetailerAccountId] = useState<
    string | null
  >(null)
  const [selectedInvitation, setSelectedInvitation] =
    useState<RegistrationKey | null>()
  const [processingRequest, setProcessingRequest] = useState(false)
  const [runQuery, { data, loading, refetch }] =
    useLazyQuery<Query>(GET_PENDING_INVITES)
  const [createRetailer] = useMutation(CREATE_RETAILER)
  const [joinRetailer] = useMutation(JOIN_RETAILER)
  const [addRetailerToRetailerAccount] = useMutation(
    ADD_RETAILER_TO_RETAILER_ACCOUNT,
  )
  const [getBrandSocialSettings] = useLazyQuery(GET_BRAND_SOCIAL_SETTING)
  const [getJwt] = useLazyQuery(GET_JWT, {
    fetchPolicy: 'no-cache',
  })
  const headers = useSelector((state) => state.auth.headers)
  const params = manageBrandsPaths.index.useParams()
  const { enqueueSnackbar } = useSnackbar()
  const [disableRegistrationKey] = useMutation<Mutation>(
    DISABLE_REGISTRATION_KEY,
  )
  const [validateToken] = useLazyQuery(VALIDATE_TOKEN)

  const { showManageBrandDialog } = useManageBrandDialog({})
  useEffect(() => {
    runQuery({
      variables: {
        userId: currentUser.id,
      },
    })
  }, [currentUser.id, runQuery])

  /**
   * Normally this dispatch would be within the handleRetailerClick call, but after some testing,
   * the dispatch call is not running the UPDATE_URL side effect required to persist the retailer selection change.
   */
  useEffect(() => {
    if (acceptedRetailerAccountId !== null && acceptedRetailerId !== null) {
      // refetched so the user state is properly updated with new retailer, causes a flash to occur on the screen
      validateToken().then(({ data }) => {
        if (data && data.validateToken) {
          dispatch({
            type: 'SIGN_IN_FULFILLED',
            payload: {
              user: data.validateToken.user,
            },
          })
        }
      })
      dispatch(
        actions.app.selectRetailerAccount({
          id: Number(acceptedRetailerAccountId),
          skipUrlUpdate: false,
        }),
      )
      getBrandSocialSettings({
        variables: {
          brandId: selectedInvitation?.brand_id,
          params: {
            fields: ['settings'],
          },
        },
      }).then(({ data }) => {
        const subtitle = data?.getBrand?.settings
          .social_account_synchronization_on_creation
          ? I18n.t(
              'manageBrands.pendingInvitesDialog.manageBrandDialogSubtitleSync',
              { brandName: selectedInvitation?.brand?.name },
            )
          : I18n.t(
              'manageBrands.pendingInvitesDialog.manageBrandDialogSubtitleNoSync',
              { brandName: selectedInvitation?.brand?.name },
            )
        showManageBrandDialog({
          retailerId: String(acceptedRetailerId),
          subtitle,
        })
      })
      setAcceptedRetailerId(null)
      setAcceptedRetailerAccountId(null)
      setSelectedInvitation(null)
    }
  }, [
    acceptedRetailerId,
    acceptedRetailerAccountId,
    selectedInvitation,
    dispatch,
    showManageBrandDialog,
    getBrandSocialSettings,
    validateToken,
  ])

  let children: React.ReactNode = null

  if (loading) {
    children = null
  } else {
    const results = data?.getUser?.registration_keys

    if (results?.length === 0) {
      children = (
        <Box padding={4} textAlign="center">
          <Typography color="textSecondary">
            {I18n.t('manageBrands.pendingInvitesPage.noInvites')}.{' '}
            <LinkChild
              to={manageBrandsPaths.index.build({
                brandSlug: params.brandSlug,
                retailerAccountSlug: params.retailerAccountSlug,
                action: 'add',
              })}
            >
              <Link>
                {I18n.t('manageBrands.pendingInvitesPage.findMoreContent')}
              </Link>
            </LinkChild>
          </Typography>
        </Box>
      )
    } else {
      children = results?.map((registrationKey) => {
        return (
          <PendingInviteListItem
            key={registrationKey.id}
            invitation={registrationKey}
            onClickAccept={() => {
              setSelectedInvitation(registrationKey)
              setDialogOpen(true)
            }}
            onClickReject={() => {
              setSelectedInvitation(registrationKey)
              openModal()
            }}
          />
        )
      })
    }
  }

  const handleRetailerClick = async (retailerAccount: RetailerAccount) => {
    setProcessingRequest(true)
    try {
      let retailerId = selectedInvitation?.retailer_id
      if (!retailerId) {
        const newRetailer = {
          name: retailerAccount.name,
          phone: retailerAccount.phone,
          phone_extension: retailerAccount.phone_extension,
          website: retailerAccount.website,
          brand_id: selectedInvitation?.brand_id,
        }
        const createdRetailer = await createRetailer({
          variables: {
            params: newRetailer,
            retailerAccountId: retailerAccount.id,
          },
        })
        retailerId = createdRetailer?.data?.createRetailer?.id
      } else {
        await joinRetailer({
          variables: {
            brandId: selectedInvitation?.brand_id,
            linkId: selectedInvitation?.link_id,
          },
        })
      }
      const getJwtResponse = await getJwt({
        variables: {
          accessToken: headers['access-token'],
          client: headers.client,
          expiry: headers.expiry,
          tokenType: headers['token-type'],
          uid: headers.uid,
        },
      })
      dispatch({
        type: 'ADD_JWT_TO_STATE',
        payload: {
          jwt: getJwtResponse.data.getJwt.jwt,
        },
      })
      await addRetailerToRetailerAccount({
        variables: {
          params: {
            retailerAccountId: retailerAccount.id,
            enrollment_method: 'manage_pending_invites',
            registration_key_link_id: selectedInvitation?.link_id,
            retailerId,
          },
        },
      })

      refetch()
      // Set the retailer Id to update the retailer account
      setAcceptedRetailerAccountId(retailerAccount.id)
      setAcceptedRetailerId(retailerId || null)
    } catch (err) {
      enqueueSnackbar(
        I18n.t('manageBrands.pendingInvitesDialog.errorConnectingRetailer'),
        {
          variant: 'error',
        },
      )
      console.error(err)
    }
    setProcessingRequest(false)
    setDialogOpen(false)
  }

  const { isOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <Container maxWidth="md" disableGutters>
        <Paper>
          <Box padding={4}>
            <Typography variant="h6">
              {I18n.t('manageBrands.pendingInvites')}
            </Typography>
          </Box>
          {loading ? <LinearProgress /> : undefined}
          <TableContainer>
            <Table>{children}</Table>
          </TableContainer>
        </Paper>
      </Container>

      <ConfirmDialog
        open={isOpen}
        onClose={closeModal}
        content={I18n.t(
          'manageBrands.pendingInvitesPage.rejectInviteConfirmation',
          { brandName: selectedInvitation?.brand?.name },
        )}
        onConfirmClick={async () => {
          if (!selectedInvitation) {
            return
          }

          try {
            await disableRegistrationKey({
              variables: {
                brand_id: Number(selectedInvitation.brand_id),
                user_id: Number(currentUser.id),
              },
            })
            enqueueSnackbar(
              I18n.t('manageBrands.pendingInvitesPage.rejectInviteSuccess', {
                brandName: selectedInvitation.brand?.name,
              }),
              {
                variant: 'success',
              },
            )
          } catch (err) {
            console.error(err)
            enqueueSnackbar(I18n.t('shared.somethingWentWrong'), {
              variant: 'error',
            })
          }

          refetch()
        }}
      />

      <MobileFullScreenDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="flex-start"
            alignContent="center"
            flexWrap="nowrap"
          >
            <SmartImage
              className={classes.image}
              alt={selectedInvitation?.brand?.name || ''}
              fallbackSrc={cloudinary
                .instance()
                .url('preview-defaults/campaign-default')}
              src={selectedInvitation?.brand?.logo_url}
            />
            <div>
              <Typography variant="h5">
                {I18n.t('manageBrands.pendingInvitesDialog.title', {
                  brandName: selectedInvitation?.brand?.name,
                })}
              </Typography>
              <Typography variant="subtitle2">
                {I18n.t('manageBrands.pendingInvitesDialog.description', {
                  brandName: selectedInvitation?.brand?.name,
                })}
                <a
                  href="https://support.promoboxx.com/hc/en-us/articles/15363099197595-Brand-Invitations-Unlock-Content-for-Multiple-Businesses"
                  target="_blank"
                  rel="noreferrer"
                >
                  {I18n.t('manageBrands.pendingInvitesDialog.descriptionLink')}
                </a>
              </Typography>
            </div>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6">
            {I18n.t('manageBrands.pendingInvitesDialog.select')}
          </Typography>
          <Divider />
          <BusinessSelector
            working={processingRequest && selectedInvitation}
            disabledCondition={(retailerAccount: RetailerAccount) => {
              return retailerAccount?.retailers?.find((retailer) => {
                return (
                  String(retailer?.brand?.id) ===
                  String(selectedInvitation?.brand_id)
                )
              })
            }}
            handleRetailerClick={handleRetailerClick}
            secondaryText={
              <Typography variant="body2" color="error">
                {I18n.t(
                  'manageBrands.pendingInvitesDialog.existingRetailerConnection',
                  { brandName: selectedInvitation?.brand?.name },
                )}
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedInvitation(null)
              setDialogOpen(false)
            }}
          >
            {I18n.t('shared.close')}
          </Button>
        </DialogActions>
      </MobileFullScreenDialog>
      <BottomAppBarSpacer />
    </>
  )
}

const PendingInviteListItem: React.FC<
  React.PropsWithChildren<{
    invitation: RegistrationKey
    onClickAccept: (invitation: RegistrationKey) => void
    onClickReject: (invitation: RegistrationKey) => void
  }>
> = (props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement>()

  const handleClose = () => {
    setAnchorEl(undefined)
  }

  return (
    <TableRow key={props.invitation.brand?.id}>
      <TableCell>
        <Box display="flex" alignItems="center">
          <ChildSpacer>
            {props.invitation.brand?.logo_url && (
              <img
                src={props.invitation.brand?.logo_url}
                alt={props.invitation.brand?.name || 'Logo'}
                width={50}
              />
            )}
            <ListItemText primary={props.invitation.brand?.name} />
          </ChildSpacer>
        </Box>
      </TableCell>

      <TableCell style={{ width: 200, textAlign: 'right' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            props.onClickAccept(props.invitation)
          }}
        >
          {I18n.t('manageBrands.pendingInvitesPage.acceptInvite')}
        </Button>
      </TableCell>
      <TableCell style={{ width: 50 }}>
        {
          <>
            <IconButton
              onClick={(event) => setAnchorEl(event.currentTarget)}
              size="large"
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={() => {
                  props.onClickReject(props.invitation)
                  handleClose()
                }}
              >
                <ListItemIcon>
                  <RemoveCircle />
                </ListItemIcon>
                {I18n.t('manageBrands.pendingInvitesPage.rejectInvite')}
              </MenuItem>
            </Menu>
          </>
        }
      </TableCell>
    </TableRow>
  )
}

export default PendingInvites
